import { bind, debounce } from 'decko'
import { LiveElement, LiveNodeList } from 'live-node-list'

export default class Header {
  /**
   * @type {LiveElement}
   */
  header = new LiveElement('.header-section')

  /**
   * @type {LiveElement}
   */
  mainHeader = new LiveElement('.main-header')

  /**
   * @type {LiveElement}
   */
  promoBar = new LiveElement('.promo-bar')

  /**
   * @type {LiveElement}
   */
  offScreenNav = new LiveElement('.main-nav')

  /**
   * @type {LiveElement}
   */
  burger = new LiveElement('.burger')

  /**
   * @type {LiveElement}
   */
  burgerLabel = new LiveElement('.burger__label')

  /**
   * @type {LiveNodeList}
   */
  mobileNavLinks = new LiveNodeList(
    '.main-nav__list-item--subnav .main-nav__list-link, .main-nav__child-list-item--subnav .main-nav__child-list-link',
  )

  /**
   * @type {LiveNodeList}
   */
  mobileNavBackLinks = new LiveNodeList(
    '.main-nav__child-list-back, .main-nav__grandchild-list-back',
  )

  /**
   * @type {LiveNodeList}
   */
  subNavs = new LiveNodeList('.main-nav__child-list')

  /**
   * @type {Number}
   */
  lastScrollPosition = 0

  /**
   * @type {Number}
   */
  minWidth = 1020

  /**
   * @type {Array}
   */
  transparentTemplates = ['template-index']

  /**
   * @type {LiveNodeList}
   */
  searchButtons = new LiveNodeList('.main-header-search-button')

  /**
   * @type {LiveElement}
   */
  search = new LiveElement('.main-header-search')

  /**
   * @type {LiveElement}
   */
  searchClose = new LiveElement('.main-header-search__close')

  /**
   * @type {LiveElement}
   */
  searchInput = new LiveElement('.search-form-input')

  constructor() {
    this.registerListeners()
  }

  @bind
  registerListeners() {
    this.burger.addEventListener('click', this.toggleNav)
    this.searchButtons.addEventListener('click', this.openSearch)
    this.searchClose.addEventListener('click', this.closeSearch)
    this.toggleScrollHeader()

    this.mobileNavLinks.addEventListener('click', this.toggleSubNav)
    this.mobileNavBackLinks.addEventListener('click', this.closeSubNav)

    document.addEventListener('click', event => {
      this.closeAllSubNavs()
    })
    this.subNavs.addEventListener('click', event => {
      event.stopPropagation()
    })
    this.mobileNavLinks.addEventListener('click', event => {
      event.stopPropagation()
    })

    window.addEventListener(
      'scroll',
      () => {
        this.toggleScrollHeader()
      },
      { passive: true },
    )
  }

  @bind
  toggleNav(e) {
    e.preventDefault()

    if (document.body.classList.contains('nav-open')) {
      this.closeNav()
    } else {
      this.openNav()
    }
  }

  @bind
  openNav() {
    document.body.classList.add('nav-open')
    document.body.classList.add('lock-scroll')
    this.offScreenNav.item.setAttribute('aria-hidden', 'false')
    this.burger.item.setAttribute('aria-expanded', 'true')
    this.changeLabelText('Close menu')
  }

  /**
   *
   */
  @bind
  closeNav() {
    document.body.classList.remove('nav-open')
    document.body.classList.remove('lock-scroll')
    this.offScreenNav.item.setAttribute('aria-hidden', 'true')
    this.burger.item.setAttribute('aria-expanded', 'false')
    this.changeLabelText('Open menu')
  }

  /**
   *
   */
  changeLabelText(text = 'Open menu') {
    this.burgerLabel.item.innerHTML = text
    this.burger.item.title = this.burgerLabel.item.innerText
  }

  /**
   *
   */
  @bind
  async toggleScrollHeader() {
    const lastY = this.lastScrollPosition
    const y = window.pageYOffset
    const h = this.header?.clientHeight || 0
    const p = this.promoBar?.clientHeight || 0

    if (y > h + p && y > lastY) {
      if (!this.header.item.classList.contains('header-section--sticky')) {
        this.header.item.classList.add('header-section--sticky')
        setTimeout(() => {
          this.header.item.classList.add('header-section--transition')
        }, 50)
      }
    } else if (y <= lastY && y <= p) {
      this.header.item.classList.remove('header-section--sticky')
      this.header.item.classList.remove('header-section--transition')
    }

    if (y > lastY) {
      this.header.item.classList.add('header-section--hidden')
      document.body.style.setProperty('--dynamic-header-size', '0')
    } else if (y < lastY) {
      this.header.item.classList.remove('header-section--hidden')
      document.body.style.removeProperty('--dynamic-header-size')
    }

    this.lastScrollPosition = window.pageYOffset
  }

  @bind
  openSearch(e) {
    e.preventDefault()
    this.search.item.classList.add('main-header-search--active')
    this.mainHeader.item.classList.add('main-header--search-active')
    this.searchInput.item.focus()
  }

  @bind
  closeSearch(e) {
    e.preventDefault()
    this.searchClose.item.blur()
    this.search.item.classList.remove('main-header-search--active')
    this.mainHeader.item.classList.remove('main-header--search-active')
  }

  @bind
  async toggleSubNav(e) {
    if (window.matchMedia('(hover: hover)').matches) {
      return true
    }

    e.preventDefault()
    const link = e.currentTarget || e.target
    const target = link.getAttribute('aria-controls')
    const element = document.getElementById(target)

    await this.closeAllSubNavs()

    if (element) {
      const isHidden = element.getAttribute('aria-hidden') === 'true'
      element.setAttribute('aria-hidden', isHidden ? 'false' : 'true')
    }
  }

  @bind
  async openSubNav(e) {
    if (window.matchMedia('(hover: hover)').matches) {
      return true
    }

    e.preventDefault()
    const link = e.currentTarget || e.target
    const target = link.getAttribute('aria-controls')
    const element = document.getElementById(target)

    await this.closeAllSubNavs()
    element?.setAttribute('aria-hidden', 'false')
  }

  @bind
  async closeSubNav(e) {
    if (window.matchMedia('(hover: hover)').matches) {
      return true
    }

    e.preventDefault()
    const link = e.currentTarget || e.target
    const target = link.getAttribute('aria-controls')
    const element = document.getElementById(target)

    element?.setAttribute('aria-hidden', 'true')
  }

  @bind
  closeAllSubNavs() {
    return Promise.all(
      this.subNavs.map(item => {
        return new Promise(resolve => {
          item.setAttribute('aria-hidden', 'true')
          resolve()
        })
      }),
    )
  }
}
